import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { TEMPLATES } from '../data/templates.js';
import { playbookBySlug } from '../data/playbook.js';
import TemplateDownload, { NutTaiNhanh } from '../components/TemplateDownload.jsx';
import { docMauDaXuatBan, useMau } from '../lib/templates.js';
import { NHOM_MAU, NGANH_MAU, nhanMoTaMau, nganhBySlug, nhomBySlug, coBanTiengAnh } from '../lib/templateModel.js';
import { capNhatHead } from '../lib/headClient.js';
import { anhMau } from '../data/templateImages.js';
import NhanTram from '../components/NhanTram.jsx';
import { tramCuaMau, linkTram } from '../data/lo-trinh.js';
import { VIEC_CUA_NHOM, ketQuaMau, batDauMau } from '../data/templateGuide.js';
import { KHOA_NGANH } from '../lib/goiYTram.js';
import MauSong from '../components/MauSong.jsx';
import NhomMauIcon, { mauCuaNhom } from '../components/NhomMauIcon.jsx';
import { docChon, ghiChon, locVaDem } from '@shared/locMuc.js';
import { KhungHaiCot, NhomLoc, OTim, DongDem } from '../components/LocHaiCot.jsx';
import { normalize } from '../lib/workspace.js';

// Dashboard tương tác tải riêng, chỉ khi mở đúng trang.
const DASHBOARD = {
  'dashboard-bao-cao-nha-dau-tu': lazy(() => import('../components/dashboard/DashboardBaoCao.jsx')),
  'dashboard-dong-tien-13-tuan': lazy(() => import('../components/dashboard/DashboardDongTien.jsx')),
  'dashboard-chuoi-fnb': lazy(() => import('../components/dashboard/DashboardFnb.jsx')),
  'dashboard-thuong-mai-dien-tu': lazy(() => import('../components/dashboard/DashboardTmdt.jsx')),
  'dashboard-cong-no-phai-thu': lazy(() => import('../components/dashboard/DashboardCongNo.jsx')),
};

const HOST = 'https://fundraising.vn';

// Tên ngắn cho chip lọc (tên đầy đủ nằm ở title và trên thẻ).
const TEN_CHIP = { dashboard: 'Dashboard', 'mo-hinh-tai-chinh': 'Mô hình tài chính', 'bao-cao-tai-chinh': 'Báo cáo', 'dinh-gia': 'Định giá', 'co-phan': 'Cổ phần', 'chi-so-hoat-dong': 'KPI', 'due-diligence': 'Thẩm định', 'quan-ly-goi-von': 'Tiếp cận quỹ' };

function NhanMau({ mau }) {
  const nganh = (mau.nganh || []).map((n) => nganhBySlug(n)?.label).filter(Boolean);
  return <p className="mau-nhan">
    {nganh.map((n) => <span key={n}>{n}</span>)}
    {nhanMoTaMau(mau) && <span>{nhanMoTaMau(mau)}</span>}
    {coBanTiengAnh(mau) && <span className="mau-nhan-en" title="Có bản tiếng Anh để gửi nhà đầu tư nước ngoài">Có bản tiếng Anh</span>}
  </p>;
}

// Lộ trình dùng mã ngành riêng cho Trạm 2; đổi sang mã ngành của thư viện mẫu.
const NGANH_TU_LO_TRINH = { saas: 'saas', fnb: 'fnb', tmdt: 'ecommerce' };

/** Thẻ mẫu cùng kiểu thẻ công cụ (/cong-cu): biểu tượng nhóm, tên, việc mẫu giúp làm, phạm vi. */
function TheMau({ t }) {
  const nganh = (t.nganh || []).map((n) => nganhBySlug(n)?.label).filter(Boolean);
  const tram = tramCuaMau(t.slug);
  return <Link className="cct mauc" id={`mau-${t.slug}`} to={`/mau/${t.slug}`} style={{ '--mau': mauCuaNhom(t.nhom) }}>
    <span className="cct-dau">
      <span className="cct-icon"><NhomMauIcon slug={t.nhom} /></span>
      <span className="cct-tram">{tram && tram.so > 0 ? `Trạm ${tram.so}` : nhomBySlug(t.nhom)?.label}</span>
    </span>
    <span className="cct-ten">{t.title}</span>
    <span className="cct-hoi mauc-ket-qua">{ketQuaMau(t)}</span>
    <span className="mauc-pham-vi">{[nganh.join(', ') || 'Mọi ngành', nhanMoTaMau(t)].filter(Boolean).join('. ')}</span>
    <span className="cct-chan">
      {coBanTiengAnh(t) ? <span className="cct-nen mauc-en" title="Có bản tiếng Anh để gửi nhà đầu tư nước ngoài">Có bản tiếng Anh</span> : <span>Tiếng Việt</span>}
      <span className="cct-dung">{t.kind === 'dashboard' ? 'Mở dashboard →' : 'Xem và tải →'}</span>
    </span>
  </Link>;
}

/** Hai mẫu SaaS giải quyết hai việc khác nhau — nói rõ chọn bản nào. */
function ChonBanSaas({ list }) {
  const ngan = list.find((t) => t.slug === 'mo-hinh-tai-chinh-saas');
  const dai = list.find((t) => t.slug === 'mo-hinh-tai-chinh-36-thang-saas');
  if (!ngan || !dai) return null;
  return <aside className="chon-ban" aria-label="Chọn bản mô hình SaaS">
    <p className="chon-ban-tieu-de">Có hai mẫu SaaS. Chọn theo việc bạn cần và dữ liệu đang có:</p>
    <ul>
      <li><strong>Muốn thử nhanh giả định</strong> về khách mới, tỷ lệ rời đi và chi phí để biết tiền còn đủ bao lâu, chưa cần báo cáo kế toán → <Link to={`/mau/${ngan.slug}`}>mẫu SaaS 12 tháng</Link> (4 tab, chỉ nhập ở một tab).</li>
      <li><strong>Cần kế hoạch để gửi nhà đầu tư</strong>, có tuyển dụng theo vị trí, ba báo cáo tài chính và nhu cầu vốn theo tháng → <Link to={`/mau/${dai.slug}`}>mô hình 36 tháng</Link> (8 sheet).</li>
    </ul>
    <p className="nho muted">Nhiều founder bắt đầu bằng bản 12 tháng, rồi chuyển số sang bản 36 tháng khi chuẩn bị hồ sơ.</p>
  </aside>;
}

export function TemplateList() {
  // Bắt đầu bằng danh sách đóng gói (khớp HTML prerender), rồi bổ sung mẫu
  // vừa xuất bản trên Firestore mà chưa kịp deploy.
  const [list, setList] = useState(TEMPLATES);
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const nhomChonDs = docChon(params, 'nhom'), nganhChon = docChon(params, 'nganh'), enChon = docChon(params, 'en');
  const doiNhom = (key) => (ds) => setParams(ghiChon(params, key, ds), { replace: true });
  const dsMau = useRef(null);
  useEffect(() => { let live = true; docMauDaXuatBan().then((all) => { if (live) setList(all); }); return () => { live = false; }; }, []);
  // Người đã chọn ngành ở Trạm 2 của Lộ trình thì chọn sẵn ngành đó (khi URL chưa chọn gì).
  useEffect(() => {
    try {
      const n = NGANH_TU_LO_TRINH[window.localStorage.getItem(KHOA_NGANH) || ''];
      if (n && !params.get('nganh')) setParams(ghiChon(params, 'nganh', [n]), { replace: true });
    } catch { /* bỏ qua */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const coNganh = NGANH_MAU.filter((n) => n.slug !== 'chung' && list.some((t) => t.nganh?.includes(n.slug)));
  const nhomTatCa = NHOM_MAU.filter((n) => list.some((t) => t.nhom === n.slug));
  // Mẫu "Mọi ngành" khớp với ngành nào cũng được, nên đếm vào mọi ngành.
  const nganhCua = (t) => ((t.nganh || []).includes('chung') ? coNganh.map((n) => n.slug) : t.nganh || []);
  const thuTuNhom = (t) => NHOM_MAU.findIndex((n) => n.slug === t.nhom);
  const { ketQua, dem } = locVaDem(
    list,
    [
      { key: 'nhom', giaTri: (t) => [t.nhom] },
      { key: 'nganh', giaTri: nganhCua },
      { key: 'en', giaTri: (t) => (coBanTiengAnh(t) ? ['co'] : []) },
    ],
    { nhom: nhomChonDs, nganh: nganhChon, en: enChon },
    (t) => normalize(`${t.title} ${t.description || ''} ${ketQuaMau(t) || ''}`).includes(normalize(q)),
  );
  const cacMau = [...ketQua].sort((a, b) => thuTuNhom(a) - thuTuNhom(b));
  const soMau = cacMau.length;
  const nhomChon = nhomChonDs.length === 1 ? nhomChonDs[0] : '';
  const nganh = nganhChon.length === 1 ? nganhChon[0] : '';
  const tenNganh = nganh ? NGANH_MAU.find((n) => n.slug === nganh)?.label : '';
  const dangLoc = Boolean(q || nhomChonDs.length || nganhChon.length || enChon.length);
  const xoaLoc = () => setParams({}, { replace: true });
  return <>
    {/* Đầu trang: lời giới thiệu + "bảng tính sống" (đổi một giả định, cả bảng
        tự tính lại). Thẻ sheet dưới đáy bảng là bộ lọc theo việc cần làm. */}
    <div className="mau-dau ms-dau">
      <header>
        <p className="eyebrow">MẪU EXCEL</p>
        <h1>Mẫu Excel cho từng việc chuẩn bị gọi vốn</h1>
        <p className="lead">{list.length} mẫu có sẵn công thức: bạn chỉ nhập giả định, bảng tự tính phần còn lại. Tải miễn phí, không cần tài khoản.</p>
      </header>
      <MauSong
        nhom={nhomTatCa.map((n) => ({ slug: n.slug, ten: n.label, so: list.filter((t) => t.nhom === n.slug).length }))}
        dangChon={nhomChon}
        onChon={(v) => {
          setParams(ghiChon(params, 'nhom', v ? [v] : []), { replace: true });
          if (v) requestAnimationFrame(() => dsMau.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }}
      />
    </div>

    <div ref={dsMau}>
      <KhungHaiCot
        soDangChon={nhomChonDs.length + nganhChon.length + enChon.length}
        boLoc={<>
          <NhomLoc tieuDe="Việc cần làm" chon={nhomChonDs} onDoi={doiNhom('nhom')}
            ds={nhomTatCa.map((n) => ({ v: n.slug, nhan: TEN_CHIP[n.slug] || n.label, title: VIEC_CUA_NHOM[n.slug] || n.label, dem: dem.nhom[n.slug] || 0, mau: mauCuaNhom(n.slug) }))} />
          {coNganh.length > 1 && <NhomLoc tieuDe="Mô hình kinh doanh" chon={nganhChon} onDoi={doiNhom('nganh')}
            ds={coNganh.map((n) => ({ v: n.slug, nhan: n.label, dem: dem.nganh[n.slug] || 0 }))} />}
          <NhomLoc tieuDe="Ngôn ngữ" tatCa="Mọi bản" chon={enChon} onDoi={doiNhom('en')}
            ds={[{ v: 'co', nhan: 'Có bản tiếng Anh', dem: dem.en.co || 0 }]} />
        </>}
      >
        <OTim id="mau-tim" nhan="Tìm mẫu" value={q} onChange={(v) => setParams(ghiChon(params, 'q', v ? [v] : []), { replace: true })} placeholder="Tìm: dòng tiền, cap table, term sheet…" />
        <DongDem dangLoc={dangLoc} onXoa={xoaLoc}>
          Hiển thị <b>{soMau}</b> trên {list.length} mẫu{tenNganh ? `, gồm cả mẫu dùng cho mọi ngành` : ''}. Ngành bạn chưa có ở đây? Bắt đầu từ <Link to="/playbook/xay-mo-hinh-tai-chinh-cho-startup">cấu trúc mô hình tài chính chung</Link>.
        </DongDem>
        {(nhomChon === 'mo-hinh-tai-chinh' || nganh === 'saas') && <ChonBanSaas list={cacMau} />}
        {soMau > 0 ? <div className="cc-luoi mau-luoi">
          {cacMau.map((t) => <TheMau t={t} key={t.slug} />)}
        </div> : <div className="empty-state">
          <p>Chưa có mẫu cho lựa chọn này.</p>
          <button type="button" className="nut phu" onClick={xoaLoc}>Xem tất cả mẫu</button>
        </div>}
      </KhungHaiCot>
    </div>
    <section className="surface section-space">
      <h2>Trong mẫu có gì?</h2>
      <ul>
        <li>Ô nhập tách riêng khỏi ô kết quả. Mỗi tệp ghi rõ ô nào bạn được sửa; ô kết quả là công thức, bấm vào để xem cách tính.</li>
        <li>Số điền sẵn chỉ là ví dụ để bạn thấy bảng chạy ra sao. Thay bằng số của bạn và ghi nguồn trước khi gửi.</li>
        <li>Tiền Việt, ghi chú tiếng Việt, có số phiên bản và ngày cập nhật ở trang từng mẫu.</li>
        <li>Mẫu có nhãn “có bản tiếng Anh” đi kèm bản tiếng Anh cùng cấu trúc và công thức, để gửi nhà đầu tư nước ngoài.</li>
      </ul>
    </section>
  </>;
}

export function TemplateDetail() {
  const { slug } = useParams();
  const { mau, loading } = useMau(slug);
  const { hash } = useLocation();
  // Nút "Tải Excel" ở danh sách dẫn tới #tai-mau: cuộn tới khối tải khi mẫu đã hiện.
  useEffect(() => {
    if (!loading && mau && hash === '#tai-mau') {
      requestAnimationFrame(() => document.getElementById('tai-mau')?.scrollIntoView({ block: 'start' }));
    }
  }, [loading, mau, hash]);

  // Mẫu vừa xuất bản chưa có HTML tĩnh: Firebase phục vụ khung /mau/_ban-moi,
  // trang tự đặt lại head khi tải xong.
  useEffect(() => {
    if (loading) return;
    if (!mau) { capNhatHead({ title: 'Không có mẫu này | Bộ công cụ gọi vốn', noindex: true }); return; }
    capNhatHead({ title: `${mau.title} (${mau.kind === 'dashboard' ? 'dùng' : 'tải'} miễn phí) | Bộ công cụ gọi vốn`, description: mau.excerpt, canonical: `${HOST}/mau/${mau.slug}` });
  }, [mau, loading]);

  if (loading) return <div className="surface section-space"><p>Đang mở mẫu…</p></div>;
  if (!mau) return <section className="surface section-space empty-state"><h1>Không có mẫu này</h1><Link className="nut" to="/mau">Xem các mẫu khác</Link></section>;
  const bai = mau.baiLienQuan ? playbookBySlug(mau.baiLienQuan) : null;
  if (mau.kind === 'dashboard') return <TrangDashboard mau={mau} bai={bai} />;
  const laTep = mau.kind === 'tep';
  return <article className="van-ban mau-detail">
    <nav className="ngay" aria-label="Breadcrumb"><Link to="/mau">Mẫu Excel</Link>{nhomBySlug(mau.nhom) && <> / {nhomBySlug(mau.nhom).label}</>}</nav>
    <NhanTram tram={tramCuaMau(mau.slug)} trang="mau" />
    <h1>{mau.title}</h1>
    <NhanMau mau={mau} />
    <NutTaiNhanh mau={mau} />
    {anhMau(mau.slug) && (
      <figure className="anh-mau">
        {/* Ảnh bìa cũ có viên "Tải miễn phí" trông như nút: bấm vào ảnh thì đưa tới khối tải. */}
        <a href="#tai-mau" aria-label={`Tới phần tải mẫu ${mau.title}`} onClick={(e) => {
          const el = document.getElementById('tai-mau');
          if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        }}>
          <img src={anhMau(mau.slug)} alt={`Xem trước mẫu: ${mau.title}`} width="1600" height="900" fetchpriority="high" decoding="async" />
        </a>
        <figcaption>Ảnh minh hoạ nội dung mẫu, không phải ảnh chụp tệp. Mọi con số chỉ là ví dụ.</figcaption>
      </figure>
    )}
    <p className="tom-tat">{mau.excerpt}</p>
    <dl className="mau-tom-luoc">
      <div><dt>Bạn nhận được</dt><dd>{ketQuaMau(mau)}</dd></div>
      {mau.bienSo?.length > 0 && <div><dt>Cần chuẩn bị</dt><dd>{mau.bienSo.length} nhóm số liệu, liệt kê bên dưới. Chưa có số thật thì ước lượng trước, ghi rõ là ước lượng.</dd></div>}
    </dl>
    <div id="tai-mau" className="mau-tai-neo"><TemplateDownload mau={mau} hienChiTiet /></div>
    {batDauMau(mau) && <>
      <h2>Sau khi tải: bắt đầu từ đâu</h2>
      <ol>{batDauMau(mau).map((x) => <li key={x}>{x}</li>)}</ol>
    </>}
    {mau.bienSo?.length > 0 && <>
      <h2>Cần chuẩn bị</h2>
      <p>Mẫu có số ví dụ để bạn thấy bảng chạy ra sao, nhưng con số của bạn mới là thứ nhà đầu tư đọc:</p>
      <ul>{mau.bienSo.map((x) => <li key={x}>{x}</li>)}</ul>
    </>}
    {laTep
      ? mau.luuY?.length > 0 && <><h2>Lưu ý khi dùng mẫu</h2><ul>{mau.luuY.map((x) => <li key={x}>{x}</li>)}</ul></>
      : <>
        <h2>Ba điều nên làm trước khi gửi bảng cho quỹ</h2>
        <ul>
          <li>Ghi nguồn cho từng giả định vào cột ghi chú. Câu hỏi đầu tiên của người đọc luôn là “số này ở đâu ra?”.</li>
          <li>Mở tab Tóm tắt và xem tháng hết tiền. Nếu tiền mặt âm trước tháng 12, kế hoạch cần thêm vốn sớm hơn bạn tưởng.</li>
          <li>Hạ tăng trưởng một nửa và nâng chi phí 20% để xem bảng còn sống được không. Quỹ sẽ tự làm việc này, nên bạn làm trước sẽ chủ động hơn.</li>
        </ul>
      </>}
    {(bai || mau.congCu?.length > 0) && <aside className="article-tools">
      <p className="eyebrow">Đi cùng mẫu này</p>
      <h2>Đọc thêm và tính thử</h2>
      <div className="hang-nut">
        {bai && <Link className="nut" to={`/playbook/${bai.slug}`}>{bai.title} →</Link>}
        {mau.congCu?.map((c) => <Link className="nut phu" to={`/cong-cu/${c.slug}`} key={c.slug}>{c.label} →</Link>)}
      </div>
    </aside>}
    <p className="article-disclaimer">Mẫu và kết quả chỉ mang tính tham khảo, không phải tư vấn đầu tư, tư vấn tài chính hay tư vấn pháp lý.</p>
  </article>;
}

/** Trang của một dashboard tương tác: không có khối tải tệp, dashboard chiếm cả bề ngang. */
function TrangDashboard({ mau, bai }) {
  const Dashboard = DASHBOARD[mau.slug];
  return <article className="db-trang">
    <nav className="ngay" aria-label="Breadcrumb"><Link to="/mau">Mẫu</Link> / Dashboard</nav>
    <header className="db-trang-dau">
      <p className="eyebrow">DASHBOARD MẪU, MIỄN PHÍ, KHÔNG CẦN TÀI KHOẢN</p>
      <h1>{mau.title}</h1>
      <p className="lead">{mau.excerpt}</p>
    </header>
    {Dashboard
      ? <Suspense fallback={<p className="db-dang-tai">Đang mở dashboard…</p>}><Dashboard /></Suspense>
      : <p>Dashboard này chưa sẵn sàng.</p>}
    <section className="db-trang-than van-ban">
      <h2>Cần chuẩn bị</h2>
      <ul>{(mau.bienSo || []).map((x) => <li key={x}>{x}</li>)}</ul>
      <h2>Bốn nguyên tắc dashboard này giữ</h2>
      <ul>
        <li><strong>Tiêu đề nói kết luận.</strong> Dòng chữ lớn trên cùng là điều người đọc cần nhớ, tên chỉ số nằm ở dòng nhỏ.</li>
        <li><strong>Cột bắt đầu từ 0.</strong> Cắt trục làm chênh lệch 5% trông như gấp đôi, nhà đầu tư có kinh nghiệm nhận ra ngay.</li>
        <li><strong>Tô màu một điểm.</strong> Tháng báo cáo màu cam, các tháng khác màu xám, để mắt dừng đúng chỗ.</li>
        <li><strong>Ô trống là chưa điền, không phải 0.</strong> Bảng ghi "Chưa điền" thay vì vẽ một cú sập không có thật.</li>
      </ul>
      {(bai || mau.congCu?.length > 0) && <aside className="article-tools">
        <p className="eyebrow">Đi cùng dashboard này</p>
        <div className="hang-nut">
          {bai && <Link className="nut" to={`/playbook/${bai.slug}`}>{bai.title} →</Link>}
          {mau.congCu?.map((c) => <Link className="nut phu" to={`/cong-cu/${c.slug}`} key={c.slug}>{c.label} →</Link>)}
        </div>
      </aside>}
      <p className="db-tham-khao">Cách chọn chỉ số và biểu đồ tham khảo <a href="https://www.christianmartinez-thefinancialfox.com/dashboard-atlas" target="_blank" rel="noopener noreferrer">CM Dashboard Atlas</a> của Christian Martinez. Thiết kế, câu chữ và số ví dụ do fundraising.vn soạn cho startup Việt.</p>
      <p className="article-disclaimer">Dashboard và kết quả chỉ mang tính tham khảo, không phải tư vấn đầu tư, tư vấn tài chính hay tư vấn pháp lý. Số bạn nhập chỉ lưu trên trình duyệt này, không gửi lên máy chủ.</p>
    </section>
  </article>;
}
